import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link2, UserPlus, Loader2, Copy, Check, Trash2 } from "lucide-react";

/** Family-portal invite list + creator. Used inside the AdminClients edit modal. */
export default function ClientFamilyInvites({ clientId, invites, setInvites }) {
  const [form, setForm] = useState({ recipient_name: "", relationship: "", ttl_days: 30 });
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const create = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    if (!clientId) return;
    if (!form.recipient_name.trim()) {
      toast.error("Recipient name is required");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        recipient_name: form.recipient_name,
        relationship: form.relationship,
        ttl_days: Number(form.ttl_days) || 30,
      };
      const { data } = await api.post(`/clients/${clientId}/family-invites`, payload);
      setInvites((arr) => [data, ...arr]);
      setForm({ recipient_name: "", relationship: "", ttl_days: 30 });
      toast.success("Family invite created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create invite");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this family link? They will no longer be able to view updates.")) return;
    try {
      await api.delete(`/family-invites/${id}`);
      setInvites((arr) => arr.filter((i) => i.id !== id));
      toast.success("Invite revoked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  const copyLink = async (token, id) => {
    const url = `${window.location.origin}/family/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1800);
      toast.success("Link copied");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  return (
    <div className="border-t border-clinical-border pt-4">
      <div className="flex items-center gap-2 mb-2">
        <Link2 className="h-4 w-4 text-clinical-primary" />
        <span className="label-eyebrow !text-clinical-primary">Family access</span>
      </div>
      <p className="text-xs text-clinical-subtext mb-3">
        Share a private read-only link with a relative. They will see the last 14 days
        of professionally formatted shift notes — never raw notes or internal data.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 mb-3">
        <input
          value={form.recipient_name}
          onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
          placeholder="Recipient name (e.g. Lisa)"
          className="input-clinical sm:col-span-2"
          data-testid="invite-recipient-input"
        />
        <input
          value={form.relationship}
          onChange={(e) => setForm({ ...form, relationship: e.target.value })}
          placeholder="Relationship (e.g. Daughter)"
          className="input-clinical sm:col-span-2"
          data-testid="invite-relationship-input"
        />
        <select
          value={form.ttl_days}
          onChange={(e) => setForm({ ...form, ttl_days: Number(e.target.value) })}
          className="input-clinical sm:col-span-1"
          data-testid="invite-ttl-select"
          title="Link expiration"
        >
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
          <option value={180}>180 days</option>
        </select>
        <button
          type="button"
          onClick={create}
          disabled={creating}
          className="btn-primary sm:col-span-1"
          data-testid="create-invite-button"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : (
            <>
              <UserPlus className="h-4 w-4" /> Invite
            </>
          )}
        </button>
      </div>

      {invites.length === 0 ? (
        <div className="text-xs text-clinical-subtext italic">No family invites yet.</div>
      ) : (
        <ul className="space-y-2" data-testid="invites-list">
          {invites.map((inv) => {
            const exp = inv.expires_at ? new Date(inv.expires_at) : null;
            const expired = exp ? exp.getTime() < Date.now() : false;
            return (
              <li
                key={inv.id}
                className="flex items-start justify-between gap-3 rounded-lg border border-clinical-border p-2.5"
                data-testid={`invite-${inv.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-clinical-text">
                    {inv.recipient_name}
                    {inv.relationship && (
                      <span className="font-normal text-clinical-subtext"> · {inv.relationship}</span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[11px] text-clinical-subtext font-mono truncate">
                    /family/{inv.token.slice(0, 12)}…
                  </div>
                  {exp && (
                    <div
                      className={`mt-0.5 text-[11px] ${expired ? "text-destructive" : "text-clinical-subtext"}`}
                      data-testid={`invite-expiry-${inv.id}`}
                    >
                      {expired ? "Expired " : "Expires "}{exp.toLocaleDateString()}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => copyLink(inv.token, inv.id)}
                    className="btn-ghost p-1.5"
                    data-testid={`copy-invite-${inv.id}`}
                    title="Copy link"
                  >
                    {copiedId === inv.id ? (
                      <Check className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => revoke(inv.id)}
                    className="btn-ghost p-1.5 hover:!text-destructive"
                    data-testid={`revoke-invite-${inv.id}`}
                    title="Revoke"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
